import { ExternalLink, FolderOpen, Hash, Mail } from 'lucide-react';
import React, { useMemo } from 'react';
import { Control, Controller, useWatch } from 'react-hook-form';

import { CmsSyncCapabilities } from '@/components/cms-sync/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  getCmsTargetSystemFromUrl,
  getDriveSystemFromUrl,
} from '@/utils/externalSystemUtils';

import { FormSection } from './FormSection';
import { CreateMatterFormValues, UpdateMatterFormValues } from './types';

type CMSIntegrationFormValues = Pick<
  CreateMatterFormValues & UpdateMatterFormValues,
  'external_cms_url' | 'external_drive_url' | 'cms_email' | 'case_id'
>;

interface CMSIntegrationSectionProps {
  control:
    | Control<CreateMatterFormValues>
    | Control<UpdateMatterFormValues>;
}

export const CMSIntegrationSection = ({
  control,
}: CMSIntegrationSectionProps) => {
  const typedControl = control as unknown as Control<CMSIntegrationFormValues>;

  // Watch the current form value for external_cms_id
  const currentExternalCmsUrl = useWatch({
    control: typedControl,
    name: 'external_cms_url',
  });

  const currentExternalDriveUrl = useWatch({
    control: typedControl,
    name: 'external_drive_url',
  });

  const currentCmsEmail = useWatch({
    control: typedControl,
    name: 'cms_email',
  });

  const displayUrl = useMemo(() => {
    try {
      return new URL(currentExternalCmsUrl ?? '').toString();
    } catch (error) {
      return null;
    }
  }, [currentExternalCmsUrl]);

  const displayDriveUrl = useMemo(() => {
    try {
      return new URL(currentExternalDriveUrl ?? '').toString();
    } catch (error) {
      return null;
    }
  }, [currentExternalDriveUrl]);

  const detectedCms = useMemo(
    () => getCmsTargetSystemFromUrl(currentExternalCmsUrl),
    [currentExternalCmsUrl],
  );

  const detectedDrive = useMemo(
    () => getDriveSystemFromUrl(currentExternalDriveUrl),
    [currentExternalDriveUrl],
  );
  const detectedSystem = detectedCms ?? detectedDrive;
  const driveLabelMap = {
    DROPBOX: 'Dropbox Folder URL',
    GOOGLE_DRIVE: 'Google Drive Folder URL',
  } as const;
  const driveDescriptionMap = {
    DROPBOX: 'Link this matter to a Dropbox folder for document sync',
    GOOGLE_DRIVE: 'Link this matter to a Google Drive folder for document sync',
  } as const;
  const driveOpenLabelMap = {
    DROPBOX: 'Dropbox',
    GOOGLE_DRIVE: 'Google Drive',
  } as const;
  const driveLabel = detectedDrive ? driveLabelMap[detectedDrive] : 'Drive Folder URL';
  const drivePlaceholder =
    detectedDrive === 'DROPBOX'
      ? 'https://www.dropbox.com/home/...'
      : 'https://drive.google.com/drive/folders/... or https://www.dropbox.com/home/...';
  const driveDescription = detectedDrive
    ? driveDescriptionMap[detectedDrive]
    : 'Link this matter to a Google Drive or Dropbox folder for document sync';
  const driveOpenLabel = detectedDrive
    ? driveOpenLabelMap[detectedDrive]
    : 'external drive';

  return (
    <FormSection
      title="CMS Integration"
      description="Link this matter to external CMS and configure correspondence tracking"
    >
      <div className="space-y-4">
        <Controller
          control={typedControl}
          name="external_cms_url"
          render={({ field }) => (
            <div className="space-y-2">
              <Label htmlFor="external_cms_url">External CMS URL</Label>
              <div className="flex gap-2">
                <Input
                  {...field}
                  id="external_cms_url"
                  placeholder="Enter external CMS URL"
                  value={field.value ?? ''}
                  onChange={(e) => field.onChange(e.target.value || null)}
                />
                {displayUrl && (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => window.open(displayUrl, '_blank')}
                    title="Open in external CMS"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                Link this matter to external CMS for quick access
              </p>
            </div>
          )}
        />

        <Controller
          control={typedControl}
          name="external_drive_url"
          render={({ field }) => (
            <div className="space-y-2">
              <Label htmlFor="external_drive_url">{driveLabel}</Label>
              <div className="flex gap-2">
                <Input
                  {...field}
                  id="external_drive_url"
                  placeholder={drivePlaceholder}
                  value={field.value ?? ''}
                  onChange={(e) => field.onChange(e.target.value || null)}
                />
                {displayDriveUrl && (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => window.open(displayDriveUrl, '_blank')}
                    title={`Open in ${driveOpenLabel}`}
                  >
                    <FolderOpen className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                {driveDescription}
              </p>
            </div>
          )}
        />

        <Controller
          control={typedControl}
          name="cms_email"
          render={({ field, fieldState }) => (
            <div className="space-y-2">
              <Label htmlFor="cms_email">Matter Email Address</Label>
              <div className="flex gap-2">
                <Input
                  {...field}
                  id="cms_email"
                  type="email"
                  placeholder="e.g., matter-12345@firm.filevine.io"
                  value={field.value ?? ''}
                  onChange={(e) => field.onChange(e.target.value || null)}
                  className={fieldState.error ? 'border-destructive' : ''}
                />
                {currentCmsEmail && !fieldState.error && (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() =>
                      window.open(`mailto:${currentCmsEmail}`, '_blank')
                    }
                    title="Compose email to this address"
                  >
                    <Mail className="h-4 w-4" />
                  </Button>
                )}
              </div>
              {fieldState.error && (
                <p className="text-sm text-destructive">
                  {fieldState.error.message || 'Please enter a valid email'}
                </p>
              )}
              <p className="text-sm text-muted-foreground">
                Unique email address from your CMS (e.g., Clio, Filevine) to
                automatically save correspondence to this matter
              </p>
            </div>
          )}
        />

        <Controller
          control={typedControl}
          name="case_id"
          render={({ field }) => (
            <div className="space-y-2">
              <Label htmlFor="case_id">Case ID</Label>
              <div className="flex gap-2">
                <Input
                  {...field}
                  id="case_id"
                  placeholder="e.g., 2737337"
                  value={field.value ?? ''}
                  onChange={(e) => field.onChange(e.target.value || null)}
                />
                {field.value && (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    title="Case ID"
                    disabled
                  >
                    <Hash className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                External case identifier from your CMS
              </p>
            </div>
          )}
        />

        <CmsSyncCapabilities detectedCms={detectedSystem} />
      </div>
    </FormSection>
  );
};
